/**
 * Guest dashboard helpers for the wedding guest dashboard (GuestDashboard).
 * RSVP totals and sorted party rows come from WWGuestManagementContext +
 * guestManagementSelectors (getWWDashboardData, buildPartyDataByContactId).
 *
 * OI imports some shared helpers from this file: copyLinkToClipboard, filterPartyData,
 * buildWWInviteFormData, isGuestNamed.
 */
import {
  ATTENDING,
  AWAITING_REPLY,
  MAYBE,
  NOT_ATTENDING,
} from "../../guests/constants";
import isEmpty from "lodash/isEmpty";
import sortBy from "lodash/sortBy";

export const getSortedPartyData = (
  guests,
  invites,
  partyDataByContactId,
  rsvps,
) => {
  // Can't display the party data without the contact data loaded
  if (isEmpty(partyDataByContactId)) {
    return [];
  }

  const rsvpsByGuestId = rsvps.reduce((obj, rsvp) => {
    /**
     * Creates structure like
     * {
     *   <guestId>: {
     *     <eventId>: {
     *       "answers": [],
     *       "attending": true,
     *       "id": <rsvpId>
     *     },
     *     <eventId2>: . . .,
     *   },
     *   <guestId2>: . . .,
     * }
     */
    if (obj[rsvp.data.guest.id] === undefined) {
      obj[rsvp.data.guest.id] = {};
    }

    obj[rsvp.data.guest.id][rsvp.data.event.id] = {
      answers: rsvp.data.answers,
      attending: rsvp.data.attending,
      id: rsvp.data.id,
      rsvpStatus: rsvp.data.rsvpStatus,
    };

    return obj;
  }, {});

  const invitesByGuestId = invites.reduce((obj, invite) => {
    /**
     * Creates structure like
     * {
     *   <guestId>: [<invite1>, <invite2>],
     *   <guestId2>: . . .,
     * }
     */
    if (invite.delete) {
      return obj;
    }

    if (obj[invite.data.guest] === undefined) {
      obj[invite.data.guest] = [];
    }

    obj[invite.data.guest].push(invite.data);

    return obj;
  }, {});

  const guestDataByContactId = guests.reduce((obj, guest) => {
    /**
     * Creates structure like this, sorted by sortKey
     * [
     *   {
     *     addressbookContactId: <contactId>,
     *     email: <email>,
     *     contactName: <contactName>,
     *     sortKey: <sortKey>,
     *     guests: [
     *       {
     *         "firstName": <firstName>,
     *         "lastName": <lastName>,
     *         "id": <guestId>,
     *         "invites": [<invite1, invite2>],
     *         "rsvps": {
     *           <eventId>: {
     *             "answers": [],
     *              "attending": true,
     *               "id": <rsvpId>
     *           },
     *           <eventId2>: . . .,
     *         }
     *       }
     *     ]
     *   },
     *   {
     *     . . .,
     *   },
     * ]
     */
    if (guest.delete) {
      return obj;
    }

    if (obj[guest.data.addressbookContactId] === undefined) {
      const partyData = partyDataByContactId[guest.data.addressbookContactId];

      if (isEmpty(partyData)) {
        // Short circuit if missing addressbook data
        return obj;
      }

      obj[guest.data.addressbookContactId] = {
        contactName: partyData.name,
        email: partyData.email,
        guests: [],
        sortKey: partyData.sortKey,
        address1: partyData.address1,
        address2: partyData.address2,
        city: partyData.locality,
        contactCreatedAt: partyData.contactCreatedAt,
        country: partyData.country,
        phoneNumber: partyData.phoneNumber,
        preferredContactMethod: partyData.preferredContactMethod,
        state: partyData.administrativeArea,
        zipCode: partyData.postalCode,
      };
    }

    obj[guest.data.addressbookContactId].guests.push({
      firstName: guest.data.firstName,
      id: guest.data.id,
      invites: invitesByGuestId[guest.data.id] ?? [],
      lastName: guest.data.lastName,
      maxParty: guest.data.maxParty,
      party: guest.data.party,
      rsvps: rsvpsByGuestId[guest.data.id] ?? {},
    });

    return obj;
  }, {});

  const partyDataList = [];

  for (const [key, value] of Object.entries(guestDataByContactId)) {
    const partySize = value.guests.length;

    // "Flatten" object to a list
    partyDataList.push({
      addressbookContactId: key,
      partySize,
      ...value,
    });
  }

  return sortBy(partyDataList, (item) => item.sortKey, ["desc"]);
};

export const filterPartyData = (
  sortedPartyData,
  searchTerm,
  selectedEvent,
  hasRepliedFilter,
  hasContactInfoFilter,
  rsvpStatusFilter,
  answersFilter,
) => {
  const filteredSortedPartyData = [];

  for (const partyData of sortedPartyData) {
    // Creating a copy, because modifying the original entry means we can't
    // un-filter after when they're cleared
    const filteredEntry = {
      ...partyData,
    };

    // Do search first before any event specific filtering
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      const contactNameMatches =
        filteredEntry.contactName.toLowerCase().indexOf(lowerSearchTerm) > -1;

      if (!contactNameMatches) {
        // Contact name does not match search term, check guests
        const hasMatchingGuest = filteredEntry.guests.some(
          (guest) =>
            guest.firstName?.toLowerCase().indexOf(lowerSearchTerm) > -1 ||
            guest.lastName?.toLowerCase().indexOf(lowerSearchTerm) > -1,
        );

        if (!hasMatchingGuest) {
          // No guests match search term either, do not add party to list
          continue;
        }
      }

      if (selectedEvent && !contactNameMatches) {
        // Contact doesn't match, only show matching guests when event is selected
        filteredEntry.guests = filteredEntry.guests.filter(
          (guest) =>
            guest.firstName?.toLowerCase().indexOf(lowerSearchTerm) > -1 ||
            guest.lastName?.toLowerCase().indexOf(lowerSearchTerm) > -1,
        );
      }
    }

    // Filter out guests/contacts not invited to the event
    if (selectedEvent) {
      // If no guests are invited, skip this contact
      if (
        !filteredEntry.guests.some(
          (guest) =>
            guest.invites.findIndex(
              (invite) => Number(selectedEvent) === invite.event,
            ) > -1,
        )
      ) {
        continue;
      }

      // Modify the guest list to only show invited guests
      filteredEntry.guests = filteredEntry.guests.filter(
        (guest) =>
          guest.invites.findIndex(
            (invite) => Number(selectedEvent) === invite.event,
          ) > -1,
      );

      // Check RSVP filters
      if (rsvpStatusFilter) {
        if (rsvpStatusFilter === MAYBE) {
          filteredEntry.guests = filteredEntry.guests.filter(
            (guest) => guest.rsvps[selectedEvent]?.rsvpStatus === "maybe",
          );
        } else {
          // WW uses attending; OI uses rsvpStatus only. Match both so OI filter tabs work.
          filteredEntry.guests = filteredEntry.guests.filter((guest) => {
            const rsvp = guest.rsvps[selectedEvent];
            if (rsvpStatusFilter === ATTENDING) {
              return rsvp?.attending === true || rsvp?.rsvpStatus === "yes";
            }
            if (rsvpStatusFilter === NOT_ATTENDING) {
              return rsvp?.attending === false || rsvp?.rsvpStatus === "no";
            }
            return rsvp?.attending === undefined;
          });
        }
      }
      if (!isEmpty(answersFilter)) {
        for (const questionId of Object.keys(answersFilter)) {
          const answerData = answersFilter[questionId];

          // Don't filter if the value to be filtered on doesn't exist
          if (isEmpty(answerData)) {
            continue;
          }

          // Multiple choice, check if answer is in the list
          filteredEntry.guests = filteredEntry.guests.filter((guest) => {
            const rsvp = guest.rsvps[selectedEvent];
            const canHaveAnswers =
              rsvp?.attending === true || rsvp?.rsvpStatus === "maybe";

            if (!rsvp || !canHaveAnswers) {
              return false;
            }

            const answer = rsvp.answers.find(
              (answer) => answer.question === Number(questionId),
            )?.text;

            if (Array.isArray(answerData)) {
              // Multiple choice, check that guest answer is in answer data
              return answerData.includes(answer);
            }

            // Open ended-check that guest answer has a value
            return (
              (answerData === "answered" && Boolean(answer)) ||
              (answerData === "unanswered" && !answer)
            );
          });
        }
      }
    } else {
      // Check summary filters
      if (hasRepliedFilter) {
        // Need to calculate each individually because a party can be updated
        // after RSVP to require an additional reply and fit both criteria
        const partyHasReplied = partyData.guests.some(
          (guest) => !isEmpty(guest.rsvps),
        );
        const partyAwaitingReply = partyData.guests.some((guest) =>
          isEmpty(guest.rsvps),
        );

        // Filter out those not matching reply filter status
        if (
          (hasRepliedFilter === "hasReply" && !partyHasReplied) ||
          (hasRepliedFilter === "awaitingReply" && !partyAwaitingReply)
        ) {
          continue;
        }
      }
      if (hasContactInfoFilter) {
        // Filter out those not matching reply filter status
        if (
          (hasContactInfoFilter === "hasEmail" && !partyData.email) ||
          (hasContactInfoFilter === "hasAddress" && !partyData.address1)
        ) {
          continue;
        }
      }
    }

    filteredSortedPartyData.push(filteredEntry);
  }

  return filteredSortedPartyData;
};

const getRsvpEventId = (rsvp) =>
  typeof rsvp.event === "object" && rsvp.event !== null
    ? rsvp.event.id
    : rsvp.event;

const getRsvpGuestId = (rsvp) =>
  typeof rsvp.guest === "object" && rsvp.guest !== null
    ? rsvp.guest.id
    : rsvp.guest;

/**
 * Email modal attendance map. Events stay as Redux `SelectedRecord` ({ data });
 * invites and RSVPs are plain models from guest-management context.
 */
export const getGuestManagementAttendanceByEventId = (
  events,
  invites,
  rsvps,
) => {
  const eventGuestMap = invites.reduce((acc, invite) => {
    const eventId = invite.event;
    const guestId = invite.guest;

    if (!acc[eventId]) {
      acc[eventId] = [];
    }
    acc[eventId].push(guestId);

    return acc;
  }, {});

  const eventIdMap = events
    ? events.reduce((acc, event) => {
        acc[event.data.id] = {};

        return acc;
      }, {})
    : {};

  const rsvpStatusByEventIdAndGuestId = rsvps.reduce((obj, rsvp) => {
    const eventId = getRsvpEventId(rsvp);
    const guestId = getRsvpGuestId(rsvp);
    if (obj[eventId]) {
      obj[eventId][guestId] = rsvp.attending ? ATTENDING : NOT_ATTENDING;
    }

    return obj;
  }, eventIdMap);

  return events.reduce((acc, event) => {
    const eventId = event.data.id;
    const guests = eventGuestMap[eventId] || [];

    acc[eventId] = guests.reduce((guestAcc, guestId) => {
      const rsvpStatus = rsvpStatusByEventIdAndGuestId[eventId][guestId];

      guestAcc[guestId] = rsvpStatus || AWAITING_REPLY;

      return guestAcc;
    }, {});

    return acc;
  }, {});
};

export const getEmailModalData = (
  events,
  invites,
  rsvps,
  site,
  sortedPartyData,
) => {
  const subject = site.isWedding
    ? `A message from ${site.siteDetails.yourName} and ${site.siteDetails.spouseName}`
    : `${site.siteDetails.eventHeading || "Event"} Update`;
  const attendanceByEventId = getGuestManagementAttendanceByEventId(
    events,
    invites,
    rsvps,
  );
  const rsvpLink = `http://${site.domain}/rsvp`;

  const guestsWithContactNameSet = [];

  for (const partyData of sortedPartyData) {
    const lastSpaceIndex = partyData.contactName.lastIndexOf(" ");
    let firstName = "";
    let lastName = "";

    if (lastSpaceIndex > -1) {
      firstName = partyData.contactName.substring(0, lastSpaceIndex);
      lastName = partyData.contactName.substring(lastSpaceIndex + 1);
    } else {
      lastName = partyData.contactName;
    }

    guestsWithContactNameSet.push({
      addressbookContactId: partyData.addressbookContactId,
      email: partyData.email,
      firstName,
      id: partyData.guests[0].id,
      lastName,
    });
  }

  const eventsWithGuestsSet = [];

  for (const event of events) {
    const eventData = event.data;
    const invitedGuestIds = new Set();

    for (const invite of invites) {
      if (invite.event === eventData.id) {
        invitedGuestIds.add(invite.guest);
      }
    }

    eventsWithGuestsSet.push({
      ...eventData,
      guests: [...invitedGuestIds]
        .map((guestId) =>
          guestsWithContactNameSet.find((guest) => guest.id === guestId),
        )
        .filter((guest) => Boolean(guest)),
    });
  }

  return {
    attendanceByEventId,
    events: eventsWithGuestsSet,
    guests: guestsWithContactNameSet,
    subject,
    invites,
    rsvpLink,
    rsvps,
  };
};

export const isGuestNamed = (guest) =>
  !isEmpty(guest.firstName?.trim()) && !isEmpty(guest.lastName?.trim());

export const isGuestOrPartyOfOneContactNamed = (
  guest,
  contactName,
  isOnlyGuest,
) =>
  isGuestNamed(guest) ||
  (Boolean(isOnlyGuest) && !isEmpty(contactName?.trim()));

export const copyLinkToClipboard = async (link) => {
  /**
   * this feature only works in secure contexts(HTTPS)
   * docs: https://developer.mozilla.org/en-US/docs/Web/API/Clipboard
   */

  if (!navigator.clipboard) {
    return false; // Clipboard API is unavailable
  }

  const formattedLink = link.startsWith("https://") ? link : `https://${link}`;

  try {
    await navigator.clipboard.writeText(formattedLink);

    return true; // Copy successful
  } catch (error) {
    console.error("Failed to copy to clipboard:", error);

    return false; // Copy failed
  }
};

/**
 * Builds InviteFormData payload from party/row data
 * Calculates which events ALL guests in the party are invited to (sharedEventIds)
 * and includes only "split" events (non-shared) in each guest's eventIds
 *
 * @param {Object} rowData - The party/row data containing all guests and contact info
 * @param {Array} events - List of all events to calculate shared event IDs
 * @returns {Object} InviteFormData payload for API submission
 */
export const buildWWInviteFormData = (rowData, events) => {
  const sharedEventIds = [];

  for (const event of events) {
    const eventId = event.data.id;
    const allInvited = rowData.guests.every(
      (guest) =>
        guest.invites.findIndex((invite) => invite.event === eventId) > -1,
    );

    if (allInvited) {
      sharedEventIds.push(eventId);
    }
  }

  const guestData = rowData.guests.map((guest) => {
    const splitEventIds = guest.invites
      .filter((invite) => !sharedEventIds.includes(invite.event))
      .map((invite) => invite.event);

    return {
      addressbookMemberId: guest.addressbookMemberId,
      eventIds: splitEventIds,
      firstName: guest.firstName || "",
      id: guest.id,
      lastName: guest.lastName || "",
      nameUnknown: !(guest.firstName && guest.lastName),
    };
  });

  return {
    addressbookContactId: rowData.addressbookContactId,
    householdName: rowData.contactName,
    guestData: guestData,
    sharedEventIds: sharedEventIds,
    address1: rowData.address1 || "",
    address2: rowData.address2 || "",
    city: rowData.city || "",
    state: rowData.state || "",
    zipCode: rowData.zipCode || "",
    country: rowData.country || "",
    email: rowData.email || "",
  };
};
